import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ICommunity } from '../../../community/model/community.model';
import { CommunityService } from '../../../community/services/community.service';
import { IPost } from '../../model/post.model';
import { PostService } from '../../services/post.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'clp-community-post-list',
    styleUrls: ['./communityPostList.component.scss'],
    templateUrl: './communityPostList.component.html',
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
        ToolbarComponent
    ],
    providers: [
        CommunityService,
        PostService,
        ToastrService
    ]
})
export class CommunityPostListComponent implements OnInit, AfterViewInit, OnDestroy {
    private communityService = inject(CommunityService);
    private postService = inject(PostService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);

    communities: ICommunity[] = [];
    posts: IPost[] = [];
    loadingPosts = false;
    selectedCommunityId = '';
    searchTerm = '';
    postToDelete: IPost | null = null;
    deleting = false;
    overflowingPostContentIndexes = new Set<number>();

    @ViewChildren('postContentBlock') private postContentBlocks!: QueryList<ElementRef<HTMLDivElement>>;
    private postContentBlocksChangesSubscription: Subscription | null = null;
    private contentResizeObserver: ResizeObserver | null = null;

    ngOnInit(): void {
        this.communityService.getCommunities().subscribe((communities) => {
            this.communities = communities ?? [];

            this.route.queryParamMap.subscribe((params) => {
                const communityId = params.get('communityId');
                if (!communityId || !this.isSelectableCommunity(communityId)) {
                    this.posts = [];
                    this.selectedCommunityId = '';
                    return;
                }

                this.selectedCommunityId = communityId;
                this.loadPosts();
            });
        });
    }

    onCommunityChange(event: Event): void {
        const communityId = (event.target as HTMLSelectElement).value;
        this.selectedCommunityId = communityId;

        if (!communityId) {
            this.posts = [];
            this.router.navigate(['/communityPosts']);
            return;
        }

        this.router.navigate(['/communityPosts'], { queryParams: { communityId } });
        this.loadPosts();
    }

    onSearchChange(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
        this.queuePostContentOverflowRecalculation();
    }

    getFilteredPosts(): IPost[] {
        const search = this.searchTerm.trim().toLowerCase();
        if (!search) {
            return this.posts;
        }

        return this.posts.filter((post) => {
            const content = this.stripHtml(post.content ?? '').toLowerCase();
            const author = (post.author ?? '').toLowerCase();
            return content.includes(search) || author.includes(search);
        });
    }

    navigateToCreate(): void {
        this.router.navigate(['/createCommunityPost'], {
            queryParams: this.selectedCommunityId ? { communityId: this.selectedCommunityId } : null
        });
    }

    editPost(post: IPost): void {
        if (!post.id) {
            return;
        }

        this.router.navigate(['/updateCommunityPost', post.id], {
            queryParams: this.selectedCommunityId ? { communityId: this.selectedCommunityId } : null
        });
    }

    openDeleteConfirmation(post: IPost): void {
        if (!post.id) {
            return;
        }

        this.postToDelete = post;
    }

    closeDeleteConfirmation(): void {
        if (this.deleting) {
            return;
        }

        this.postToDelete = null;
    }

    confirmDeletePost(): void {
        const id = this.postToDelete?.id;
        if (!id) {
            this.closeDeleteConfirmation();
            return;
        }

        this.deleting = true;
        this.postService.deletePost(id).subscribe({
            next: () => {
                this.deleting = false;
                this.postToDelete = null;
                this.toastService.show(this.translate.instant('post.delete.successMessage'), '');
                this.loadPosts();
            },
            error: () => {
                this.deleting = false;
            }
        });
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        this.closeDeleteConfirmation();
    }

    getImageUrl(fileName: string | null | undefined): string | null {
        if (!fileName) {
            return null;
        }

        return `${environment.API_URL}/posts/images/${fileName}`;
    }

    getRenderedContent(content: string | null | undefined): string {
        if (!content) {
            return '';
        }

        return this.decodeHtml(content);
    }

    isPostContentOverflow(index: number): boolean {
        return this.overflowingPostContentIndexes.has(index);
    }

    ngAfterViewInit(): void {
        this.postContentBlocksChangesSubscription = this.postContentBlocks.changes.subscribe(() => {
            this.startPostContentObservation();
            this.queuePostContentOverflowRecalculation();
        });

        this.startPostContentObservation();
        this.queuePostContentOverflowRecalculation();
    }

    ngOnDestroy(): void {
        this.postContentBlocksChangesSubscription?.unsubscribe();
        this.postContentBlocksChangesSubscription = null;
        this.contentResizeObserver?.disconnect();
        this.contentResizeObserver = null;
    }

    private loadPosts(): void {
        if (!this.selectedCommunityId) {
            this.posts = [];
            return;
        }

        this.loadingPosts = true;
        this.postService.getPostsByCommunity(this.selectedCommunityId).subscribe({
            next: (posts) => {
                this.posts = posts ?? [];
                this.loadingPosts = false;
                this.queuePostContentOverflowRecalculation();
            },
            error: () => {
                this.loadingPosts = false;
            }
        });
    }

    private stripHtml(value: string): string {
        return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    private decodeHtml(value: string): string {
        let decoded = value;

        // Some payloads arrive entity-encoded more than once (e.g. &amp;lt;p&amp;gt;).
        for (let i = 0; i < 3; i++) {
            const textarea = document.createElement('textarea');
            textarea.innerHTML = decoded;
            const next = textarea.value;

            if (next === decoded) {
                break;
            }

            decoded = next;
        }

        return decoded;
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.queuePostContentOverflowRecalculation();
    }

    private startPostContentObservation(): void {
        this.contentResizeObserver?.disconnect();
        this.contentResizeObserver = null;

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.contentResizeObserver = new ResizeObserver(() => this.calculatePostContentOverflows());
        this.postContentBlocks.forEach((contentBlock) => {
            this.contentResizeObserver?.observe(contentBlock.nativeElement);
        });
    }

    private queuePostContentOverflowRecalculation(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this.calculatePostContentOverflows());
        });
    }

    private calculatePostContentOverflows(): void {
        const nextOverflowingIndexes = new Set<number>();

        this.postContentBlocks.forEach((contentBlock) => {
            const element = contentBlock.nativeElement;
            const index = Number(element.dataset['postIndex']);
            if (!Number.isFinite(index)) {
                return;
            }

            if (element.scrollHeight - element.clientHeight > 1) {
                nextOverflowingIndexes.add(index);
            }
        });

        this.overflowingPostContentIndexes = nextOverflowingIndexes;
    }

    private isSelectableCommunity(communityId: string): boolean {
        return this.communities.some((community) => `${community.id ?? ''}` === `${communityId}`);
    }
}
